import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { cn } from './cn';

/**
 * Toast — Design System v2 (docs/design-system-v2.html §5.toast).
 *
 * API:
 *   <ToastProvider>...</ToastProvider>          ← envolva o App
 *   const { toast } = useToast();
 *   toast('Salvo com sucesso', 'success');
 *
 * Variantes (success | error | info | warning) trocam o ícone e a
 * borda esquerda. Auto-dismiss em 3.2s; click no toast também fecha.
 * Render no canto inferior direito, empilhados verticalmente.
 */

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

/** Ação opcional do toast (ex.: "Abrir"): botão à parte, e o toast fica mais tempo na tela. */
export interface ToastAcao {
  rotulo: string;
  onClick: () => void;
}

interface ToastEntry {
  id: number;
  message: ReactNode;
  variant: ToastVariant;
  acao?: ToastAcao;
}

interface ToastContextValue {
  toast: (message: ReactNode, variant?: ToastVariant, acao?: ToastAcao) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS = 3200;
/** Com ação, o usuário precisa de tempo para ler e clicar. */
const AUTO_DISMISS_COM_ACAO_MS = 9000;

const ICONS: Record<ToastVariant, JSX.Element> = {
  success: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <path d="M22 4L12 14.01l-3-3" />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  ),
};

const BORDER: Record<ToastVariant, string> = {
  success: 'border-l-success-v2 [&_.t-ic]:text-success-v2',
  error: 'border-l-danger-v2 [&_.t-ic]:text-danger-v2',
  info: 'border-l-info-v2 [&_.t-ic]:text-info-v2',
  warning: 'border-l-warning-v2 [&_.t-ic]:text-warning-v2',
};

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastEntry[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback<ToastContextValue['toast']>(
    (message, variant = 'success', acao) => {
      // Date.now é proibido em workflows mas aqui é runtime no browser — OK.
      const id = Date.now() + Math.floor(Math.random() * 1000);
      setItems((prev) => [...prev, { id, message, variant, acao }]);
      window.setTimeout(() => dismiss(id), acao ? AUTO_DISMISS_COM_ACAO_MS : AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  const ctx = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="fixed bottom-6 right-6 z-[200] flex flex-col gap-2.5 pointer-events-none"
      >
        {items.map((t) =>
          t.acao ? (
            // Com ação: contêiner (não `<button>`, que não pode conter outro botão) — clicar na
            // mensagem fecha; o botão da ação executa e fecha.
            <div
              key={t.id}
              role="status"
              onClick={() => dismiss(t.id)}
              className={cn(
                'pointer-events-auto flex cursor-pointer items-center gap-3 bg-bg-elevated border border-border-v2 border-l-[3px] rounded-md shadow-lg px-4 py-3 min-w-[300px] text-left',
                BORDER[t.variant],
                'animate-[tcl-toast-in_0.28s_var(--ease)]',
              )}
            >
              <span className="t-ic shrink-0 grid place-items-center">{ICONS[t.variant]}</span>
              <span className="text-[13px] font-medium text-text-primary">{t.message}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  t.acao!.onClick();
                  dismiss(t.id);
                }}
                className="ml-2 shrink-0 rounded-md px-2 py-1 text-[13px] font-semibold text-primary hover:bg-bg-muted"
              >
                {t.acao.rotulo}
              </button>
            </div>
          ) : (
          <button
            key={t.id}
            type="button"
            onClick={() => dismiss(t.id)}
            className={cn(
              'pointer-events-auto flex items-center gap-3 bg-bg-elevated border border-border-v2 border-l-[3px] rounded-md shadow-lg px-4 py-3 min-w-[300px] text-left',
              BORDER[t.variant],
              'animate-[tcl-toast-in_0.28s_var(--ease)]',
            )}
          >
            <span className="t-ic shrink-0 grid place-items-center">{ICONS[t.variant]}</span>
            <span className="text-[13px] font-medium text-text-primary">{t.message}</span>
          </button>
          ),
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      'useToast: faltando <ToastProvider> na árvore (registrado em App.tsx — UI-F0).',
    );
  }
  return ctx;
}
